<template>
  <x-page transparent-header white-back :loading="loading">
    <template v-if="!loading">
      <div v-if="!isOpened" slot="header" />
      <div v-if="!isOpened" :class="_.prepare">
        <x-lottie
          key="open"
          ref="lottie"
          :loop="false"
          :autoplay="false"
          :data="openAnimateData"
          :class="_.effect"
          @progress="handleLottieProgress"
          @click="handleOpenClick"
        />
        <div
          :class="_.flash"
          :style="{ opacity: isPrepared ? 1 : 0 }"
          @transitionend="handlePrepareComplete"
        />
      </div>
      <div v-else :class="[_.opened, isTrial && _.trial]">
        <div :class="_.title" />
        <div :class="_.cover">
          <img :src="openedData.commodityPic" :class="_.image" />
        </div>
        <div :class="_.congratulation">恭喜你！</div>
        <div :class="_.message">
          获得价值
          <x-price
            :class="_.price"
            unit="¥"
            :value="openedData.price"
            :unit-size="14"
            :value-size="24"
            color="#FFD859"
          />
          的商品{{ openedData.commodityTitle }}
        </div>
        <div :class="_.actions">
          <div
            v-if="!isTrial"
            v-track="{
              name: '不想要了',
              id: '2af1f256-e873-4e1c-91ee-d5692d0191f9',
            }"
            :class="_.cancel"
            @click="handleNotNeedClick"
          >
            不想要了
          </div>
          <div
            v-if="!isTrial"
            v-track="{
              name: '开心收下',
              id: '3596e5f7-a48a-4c81-b291-c64174212ba1',
            }"
            :class="_.confirm"
            @click="handleAcceptClick"
          >
            开心收下
          </div>
          <div
            v-if="isTrial"
            v-track="{
              name: '再试一次',
              id: '9456ce45-26a3-4627-955e-a5e9e6204e8e',
            }"
            :class="_.retry"
            @click="handleRetryClick"
          >
            再试一次
            <div :class="_.remain">剩{{ trialCountData }}次</div>
          </div>
          <div
            v-if="isTrial"
            v-track="{
              name: '正式开盒',
              id: '8b335f00-b647-447b-9b85-6ebeb1fc3ec8',
            }"
            :class="_.buy"
            @click="handleBuyClick"
          >
            正式开盒 ¥{{ boxData.price }}
          </div>
        </div>
        <div v-if="!isTrial" :class="_.tips">
          前往
          <div
            v-track="{
              name: '开盒页-我的盒柜',
              id: 'bcb1d1de-d309-4273-8c76-0c0c4eec6f13',
            }"
            :class="_.link"
            @click="handleMyClick"
          >
            我的盒柜
          </div>
          可选择发货或者回收，如7天后未确认，商品将自动回收。
        </div>
        <x-lottie
          v-if="!isTrial && !fromOrderList"
          key="continue"
          :data="import('./images/继续开盒动画.json')"
          :class="_.continue"
          @click="handleContinueClick"
        />
        <RefundDialog
          v-model="showRefundDialog"
          :goods="openedData"
          :order-id="orderId"
          :redirect="true"
        />
        <DeliveryDialog
          v-model="showDeliveryDialog"
          :goods="openedData"
          :order-id="orderId"
        />
      </div>
    </template>
  </x-page>
</template>

<script>
  import Vue from 'vue'
  import {
    apiBlindBoxBoxDetail,
    apiBlindBoxBoxGetTryCount,
    apiBlindBoxBoxTryOpen,
    apiBlindBoxOrderDetail,
    apiBlindBoxOrderOpen,
  } from '@/api'
  import { boxBus } from './bus'
  import { DeliveryDialog, RefundDialog } from './components'
  import { mapQuery, prepareData, submit, Track } from '@/utils'
  import { navigateBack, navigateTo, redirectTo } from '@/router'

  export default Vue.extend({
    components: {
      RefundDialog,
      DeliveryDialog,
    },

    mixins: [
      mapQuery({
        id: 'id',
        orderId: 'orderId',
        isTrial: 'isTrial',
        fromOrderList: 'fromOrderList',
      }),
      Track.provideMark(function () {
        return new Promise(resolve => {
          const off = this.$watch(
            () => this.boxData,
            () => {
              if (this.boxData.boxId) {
                off?.()
                resolve({
                  page: '盲盒开盒页',
                  id: this.boxData.boxId,
                  name: this.boxData.boxName,
                })
              }
            },
            {
              immediate: true,
            },
          )
        })
      }),
    ],

    data: () => ({
      loading: true,
      openAnimateData: {},
      boxData: {},
      openedData: {},
      trialCountData: 0,
      isOpening: false,
      isPrepared: false,
      isOpened: false,
      showRefundDialog: false,
      showDeliveryDialog: false,
    }),

    async mounted() {
      await this.prepareData()
      if (!this.isOpened) {
        this.$refs.lottie.play()
      }
    },

    activated() {
      if (this.orderId) {
        submit(async $ => {
          $.start()
          const orderDetail = await apiBlindBoxOrderDetail({
            id: this.orderId,
          })
          // 若盲盒已发货或已退款，直接返回上一页
          if (orderDetail.status === 2 || orderDetail.status === 3) {
            navigateBack()
          }
        })
      }
    },

    methods: {
      async prepareData() {
        const { openAnimateData, boxData } = await prepareData({
          openAnimateData: import('./images/开盒动画.json').then(
            res => res.default,
          ),
          boxData: apiBlindBoxBoxDetail(
            {
              id: this.id,
            },
            {
              noErrorToast: true,
            },
          ).catch(() => ({
            boxId: this.id,
            boxName: '未知盲盒',
          })),
        })
        this.openAnimateData = openAnimateData
        this.boxData = boxData
        this.loading = false
      },

      handleLottieProgress(progress) {
        if (progress >= 0.45 && !this.isOpening) {
          this.$refs.lottie.pause()
        } else if (progress >= 0.8 && this.isOpening) {
          this.isOpening = false
          this.isPrepared = true
        }
      },

      handlePrepareComplete() {
        submit(async $ => {
          $.start()
          // 试玩逻辑
          if (this.isTrial) {
            const openedData = await apiBlindBoxBoxTryOpen({
              id: this.id,
            })
            // 次数必须在开盒后面，不能用 Promise.all 优化
            const trialCountData = await apiBlindBoxBoxGetTryCount({
              id: this.id,
            })
            this.openedData = openedData
            this.trialCountData = trialCountData
            this.isOpened = true
          }
          // 正式逻辑
          else {
            const openedData = await apiBlindBoxOrderOpen({
              id: this.orderId,
            })
            const trialCountData = 0
            if (!openedData.openSuccess && openedData.jumpToDetail) {
              return redirectTo('BoxRefundResult', {
                orderId: this.orderId,
              })
            }
            this.openedData = openedData
            this.trialCountData = trialCountData
            this.isOpened = true
          }
        })
      },

      handleOpenClick() {
        this.isOpening = true
        this.$refs.lottie.play()
      },

      handleNotNeedClick() {
        this.showRefundDialog = true
      },

      handleAcceptClick() {
        this.showDeliveryDialog = true
      },

      handleRetryClick() {
        submit(async $ => {
          $.start()
          await this.prepareData()
          if (this.trialCountData === 0) {
            return $.fail('今日试玩次数已用完')
          }
          this.isPrepared = false
          this.isOpened = false
          this.$nextTick(() => {
            this.$refs.lottie.play()
          })
        })
      },

      handleBuyClick() {
        boxBus.emit('buy')
        navigateBack()
      },

      handleMyClick() {
        navigateTo('BoxOrderList')
      },

      handleContinueClick() {
        boxBus.emit('buy')
        navigateBack()
      },
    },
  })
</script>

<style lang="scss" module>
  .prepare {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #0f0118;

    .effect {
      width: 100%;
      height: 100%;
    }

    .flash {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(white, 0.9);
      transition: all ease 800ms;
      pointer-events: none;
    }
  }

  .opened {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('./images/已开盒背景_新春年货节.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: var(--page-header-height);

    .title {
      width: 178px;
      height: 68px;
      background-image: url('./images/已开盒标题_新春年货节.png');
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100% 100%;
    }

    .cover {
      width: 100%;
      height: 321px;
      padding-top: 56px;
      box-sizing: border-box;
      background-image: url('./images/已开盒相框.png');
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100% 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .image {
        width: 228px;
        height: 228px;
        border-radius: 16px;
        object-fit: cover;
      }
    }

    .congratulation {
      font-size: 24px;
      font-weight: bold;
      color: #ffffff;
      line-height: 33px;
    }

    .message {
      margin-top: 6px;
      padding: 0 12px;
      text-align: center;
      font-size: 18px;
      font-weight: bold;
      color: #ffffff;
      line-height: 25px;

      .price {
        display: inline-flex;
        margin: 0 4px;
      }
    }

    .actions {
      margin-top: 16px;
      padding: 0 6px 0 10px;
      display: flex;
      align-items: center;

      .cancel {
        width: 156px;
        height: 66px;
        padding-bottom: 11px;
        box-sizing: border-box;
        background-image: url('./images/再试一次.png');
        background-position: center;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: #ffffff;
        line-height: 25px;
      }

      .confirm,
      .buy {
        width: 202px;
        height: 66px;
        padding-bottom: 11px;
        box-sizing: border-box;
        background-image: url('./images/开心收下.png');
        background-position: center;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: #ffffff;
        line-height: 25px;
      }

      .retry {
        position: relative;
        width: 156px;
        height: 66px;
        padding-bottom: 11px;
        box-sizing: border-box;
        background-image: url('./images/不想要了_新春年货节.png');
        background-position: center;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: #740100;
        line-height: 25px;

        .remain {
          position: absolute;
          top: -10px;
          right: 21px;
          min-width: 49px;
          height: 21px;
          background: linear-gradient(270deg, #ff3d30 0%, #ff8c29 100%);
          border-radius: 10px 10px 10px 0px;
          font-size: 12px;
          color: #ffffff;
          line-height: 17px;
          padding: 2px 8px;
          box-sizing: border-box;
        }
      }
    }

    .tips {
      margin-top: 18px;
      font-size: 13px;
      color: #ffffff;
      line-height: 18px;
      padding: 0 56px;
      text-align: center;

      .link {
        display: inline-block;
        color: #88c6ff;
        text-decoration: underline;
      }
    }

    .continue {
      position: absolute;
      bottom: 190px;
      right: -10px;
      width: 100px;
    }

    &.trial {
      .actions {
        margin-top: 24px;
      }
    }
  }
</style>
